/* eslint-disable no-alert */
import React, { useState, useEffect } from 'react'
import {
  Zoning,
  ZoningType,
  ZoningReportData,
  ZoningEstimateStats,
  ZoningReference,
  ZoningMetricSources,
  zoning_indice_sources,
} from '@popety-io/types'
import {
  isEnvelopeScored,
  getEnvelopeSource,
  computeEnvelopeAuthorizedVolume,
  erodePolygonByMeters,
} from '@popety-io/utils'
import { Button } from '../../../atoms'
import { MetricSourceModal } from '../../../molecules'
import { useZoningMetrics, ZoningLandData } from '../hooks/useZoningMetrics'
import { ZoningOverridesService } from '../services/zoningOverridesService'
import {
  getMetricLabel as getMetricLabelFallback,
  getMetricUnit,
} from '../utils/zoningHelpers'
import { METRIC_DEFINITIONS } from '../utils/zoningConstants'
import {
  Section,
  SectionHeader,
  SectionTitle,
  ButtonGroup,
  Table,
  TableHead,
  TableBody,
  EmptyValue,
} from './ZoneMetricsTable.styles'
import { DeterminingAreaRow } from './DeterminingAreaRow'
import {
  EnvelopeSection,
  EnvelopeSectionTranslations,
  EnvelopeLiveValues,
} from './EnvelopeSection'
import { MetricGroupRow } from './MetricGroupRow'
import { DerivedSurfaceRow } from './DerivedSurfaceRow'
import { StandardMetricRow } from './StandardMetricRow'
import { MetricGroupHeader } from './MetricGroupHeader'
import { ConceptMetricRow } from './ConceptMetricRow'
import { AddMetricRow } from './AddMetricRow'
import { useMetricModal } from './hooks/useMetricModal'
import { useMetricCategories } from './hooks/useMetricCategories'
import {
  buildEnvelopeCalculationRows,
  EnvelopeCalcLabels,
} from './utils/envelopeCalculation'
import {
  getCurrentValueFromLandData,
  getModalProps,
  getMetricDefaultSource,
  getMetricAgreement,
  getMetricSources,
  getMetricMinDefaultSource,
  getMetricMaxDefaultSource,
  getMetricMinSources,
  getMetricMaxSources,
  getMetricMinAgreement,
  getMetricMaxAgreement,
} from './utils/metricTableHelpers'

export interface ZoneMetricsTableTranslations {
  title?: string
  resetToDefault?: string
  editAuthorizedValues?: string
  saveChanges?: string
  cancel?: string
  confirmReset?: string
  determiningLandArea?: string
  noneOption?: string
  headers?: {
    indices?: string
    current?: string
    authorized?: string
    difference?: string
  }
  groups?: {
    heights?: string
    floors?: string
    setbacks?: string
    usageRatios?: string
    environmental?: string
    other?: string
  }
  derivedSurfaces?: {
    determiningBuiltArea?: string
    determiningBuiltAreaDesc?: string
    builtArea?: string
    builtAreaDesc?: string
    determiningFloorArea?: string
    determiningFloorAreaDesc?: string
    grossUsefulFloorArea?: string
    grossUsefulFloorAreaDesc?: string
    floorArea?: string
    floorAreaDesc?: string
    builtVolume?: string
    builtVolumeDesc?: string
  }
  metricLabels?: {
    cos?: string
    cus?: string
    ibus?: string
    ios?: string
    ius?: string
    im?: string
    vbr?: string
    altitude?: string
    roofHeight?: string
    totalHeight?: string
    heightWithoutRoof?: string
    totalFloors?: string
    mainFloors?: string
    atticFloors?: string
    undergroundFloors?: string
    commercialRatio?: string
    livingRatio?: string
    officeRatio?: string
    greenRatio?: string
    noiseSensitivityLevel?: string
    bigSetback?: string
    smallSetback?: string
    buildingDepth?: string
    density?: string
    openSpaceRatio?: string
    roofSlope?: string
    distanceLimit?: string
    numberOfUnits?: string
    constructionArrangement?: string
    maxLength?: string
  }
  progressBar?: {
    currentSituation?: string
    used?: string
    authorized?: string
    utilization?: string
    remainingPotential?: string
    available?: string
  }
  unitLabels?: {
    floors?: string
    units?: string
  }
  /** Exclusive ratio concept labels (edit-mode variant selector rows) */
  concepts?: {
    occupation?: string
    utilization?: string
  }
  /** Metric visibility controls (eye toggles + add-metric chips) */
  visibility?: {
    hide?: string
    show?: string
    addMetric?: string
  }
  /** Regelbauweise envelope block (approved design: Idea B, Variant 1) */
  envelope?: EnvelopeSectionTranslations & {
    inputTag?: string
    referenceOnlySub?: string
    manualReplacesSub?: string
    s7Note?: string
    calculatedValueLabel?: string
    calcTitle?: string
    disclaimer?: string
    calc?: EnvelopeCalcLabels
  }
}

const DEFAULT_METRICS_TRANSLATIONS: ZoneMetricsTableTranslations = {
  title: 'Zoning Metrics',
  resetToDefault: 'Reset to Default',
  editAuthorizedValues: 'Edit Authorized Values',
  saveChanges: 'Save Changes',
  cancel: 'Cancel',
  confirmReset:
    'Are you sure you want to reset all values to defaults? This action cannot be undone.',
  determiningLandArea: 'Determining land area (DLA)',
  noneOption: 'None (hide this metric)',
  headers: {
    indices: 'Indices',
    current: 'Current',
    authorized: 'Authorized',
    difference: 'Difference',
  },
  groups: {
    heights: 'HEIGHTS',
    floors: 'FLOORS',
    setbacks: 'SETBACKS & DIMENSIONS',
    usageRatios: 'USAGE RATIOS',
    environmental: 'ENVIRONMENTAL',
    other: 'OTHER',
  },
  derivedSurfaces: {
    determiningBuiltArea: 'Determining built area (DBA)',
    determiningBuiltAreaDesc: 'Determining built area',
    builtArea: 'Built area (BA)',
    builtAreaDesc: 'Built area',
    determiningFloorArea: 'Determining floor area (DFA)',
    determiningFloorAreaDesc: 'Determining floor area',
    grossUsefulFloorArea: 'Gross usable floor area (GUFA)',
    grossUsefulFloorAreaDesc: 'Gross usable floor area',
    floorArea: 'Floor area (FA)',
    floorAreaDesc: 'Floor area',
    builtVolume: 'Reference building volume (RBV)',
    builtVolumeDesc: 'Reference building volume',
  },
  metricLabels: {
    cos: 'BCC',
    cus: 'FAC',
    ibus: 'GFAR',
    ios: 'BCR',
    ius: 'FAR',
    im: 'BVR',
    vbr: 'RBV',
    altitude: 'Altitude',
    roofHeight: 'Roof Height',
    totalHeight: 'Total Height',
    heightWithoutRoof: 'Height w/o Roof',
    totalFloors: 'Total Floors',
    mainFloors: 'Main Floors',
    atticFloors: 'Attic Floors',
    undergroundFloors: 'Underground Floors',
    commercialRatio: 'Commercial Ratio',
    livingRatio: 'Living Ratio',
    officeRatio: 'Office Ratio',
    greenRatio: 'Green Ratio',
    noiseSensitivityLevel: 'Noise Sensitivity Level',
    bigSetback: 'Big Setback',
    smallSetback: 'Small Setback',
    buildingDepth: 'Building Depth',
    density: 'Density',
    openSpaceRatio: 'Open Space Ratio',
    roofSlope: 'Roof Slope',
    distanceLimit: 'Distance Limit',
    numberOfUnits: 'Number of Units',
    constructionArrangement: 'Construction Arrangement',
    maxLength: 'Max Length',
  },
  progressBar: {
    currentSituation: 'Current situation',
    used: 'Used',
    authorized: 'Authorized',
    utilization: 'Utilization',
    remainingPotential: 'Remaining Potential',
    available: 'Available',
  },
  unitLabels: {
    floors: 'floors',
    units: 'units',
  },
}

/** Zone metrics that feed the Regelbauweise envelope computation */
const ENVELOPE_INPUT_KEYS = new Set([
  'building_total_height',
  'building_without_roof_height',
  'distance_limit',
  'small_setback',
  'big_setback',
  'length',
  'building_depth',
])

// Map metric keys to translation keys
export const METRIC_KEY_TO_TRANSLATION_KEY: Record<string, string> = {
  cos: 'cos',
  cus: 'cus',
  ibus: 'ibus',
  ios: 'ios',
  ius: 'ius',
  im: 'im',
  vbr: 'vbr',
  altitude: 'altitude',
  building_roof_height: 'roofHeight',
  building_total_height: 'totalHeight',
  building_without_roof_height: 'heightWithoutRoof',
  floor_nb: 'totalFloors',
  main_floor_nb: 'mainFloors',
  attic_floor_nb: 'atticFloors',
  underground_floor_nb: 'undergroundFloors',
  commercial_ratio: 'commercialRatio',
  living_ratio: 'livingRatio',
  office_ratio: 'officeRatio',
  green_ratio: 'greenRatio',
  noise_sensibility_level: 'noiseSensitivityLevel',
  big_setback: 'bigSetback',
  small_setback: 'smallSetback',
  building_depth: 'buildingDepth',
  density: 'density',
  open_space_ratio: 'openSpaceRatio',
  roof_slope: 'roofSlope',
  distance_limit: 'distanceLimit',
  unit_nb: 'numberOfUnits',
  construction_arrangement: 'constructionArrangement',
  length: 'maxLength',
}

export interface ZoneMetricsTableProps {
  zone: Zoning
  zoningType: ZoningType
  landId: string
  landData?: ZoningLandData
  zoningOverridesService?: ZoningOverridesService
  translations?: ZoneMetricsTableTranslations
  /** Read-only mode - hides edit buttons and disables inputs (for public shares) */
  readOnly?: boolean
  /** Callback to report an incorrect metric value */
  onReportIncorrect?: (data: ZoningReportData) => Promise<void>
  /** Callback to fetch estimate stats for ALG justification panel */
  onFetchEstimateStats?: (
    zoneType: string,
    zoneId: string,
    metricKey: string
  ) => Promise<ZoningEstimateStats | null>
  /** Callback when the effective distance_limit value changes (for map buffer) */
  onDistanceLimitChange?: (value: number | undefined) => void
}

export const ZoneMetricsTable: React.FC<ZoneMetricsTableProps> = ({
  zone,
  zoningType,
  landId,
  landData,
  zoningOverridesService,
  translations,
  readOnly = false,
  onReportIncorrect,
  onFetchEstimateStats,
  onDistanceLimitChange,
}) => {
  // Merge translations with defaults
  const t = {
    ...DEFAULT_METRICS_TRANSLATIONS,
    ...translations,
    headers: {
      ...DEFAULT_METRICS_TRANSLATIONS.headers,
      ...translations?.headers,
    },
    groups: {
      ...DEFAULT_METRICS_TRANSLATIONS.groups,
      ...translations?.groups,
    },
    derivedSurfaces: {
      ...DEFAULT_METRICS_TRANSLATIONS.derivedSurfaces,
      ...translations?.derivedSurfaces,
    },
    metricLabels: {
      ...DEFAULT_METRICS_TRANSLATIONS.metricLabels,
      ...translations?.metricLabels,
    },
    progressBar: {
      ...DEFAULT_METRICS_TRANSLATIONS.progressBar,
      ...translations?.progressBar,
    },
    unitLabels: {
      ...DEFAULT_METRICS_TRANSLATIONS.unitLabels,
      ...translations?.unitLabels,
    },
    concepts: translations?.concepts,
    visibility: translations?.visibility,
    envelope: translations?.envelope,
  }

  // Regelbauweise envelope gate: the land's score comes from dimensional
  // rules, not a ratio. View mode replaces the (estimated) ratio rows with
  // the envelope block; edit mode keeps them for reference / manual entry.
  const envelopeActive = isEnvelopeScored(landData?.indice_used)

  // S7: the zone defines official dimensional rules but the envelope branch
  // refused this land (e.g. untrustworthy VBr) — explain why the score is
  // still an estimate. Detectable client-side; the exact reason is not.
  const isEnvelopeFallback = (
    metrics: { key: string; max?: number; maxSource?: string }[]
  ) => {
    const officialSources = ['manual', 'geoportal', 'llm']
    const official = (key: string) => {
      const m = metrics.find((mm) => mm.key === key && mm.max !== undefined)
      return !!m && officialSources.includes(m.maxSource || '')
    }
    const hasSetback = official('distance_limit') || official('small_setback')
    const hasHeight =
      official('building_total_height') ||
      official('building_without_roof_height')
    const hasOfficialRatio = ['ibus', 'ius', 'cus', 'im', 'ios', 'cos'].some(
      official
    )
    return hasSetback && hasHeight && !hasOfficialRatio
  }

  // Helper function to get translated metric label
  const getTranslatedMetricLabel = (metricKey: string): string => {
    const translationKey = METRIC_KEY_TO_TRANSLATION_KEY[metricKey]
    if (
      translationKey &&
      t.metricLabels?.[translationKey as keyof typeof t.metricLabels]
    ) {
      return t.metricLabels[
        translationKey as keyof typeof t.metricLabels
      ] as string
    }
    // Fallback to the original getMetricLabel function
    return getMetricLabelFallback(metricKey)
  }

  // Helper function to get translated unit
  const getTranslatedUnit = (metricKey: string): string | undefined => {
    const unit = getMetricUnit(metricKey)
    if (unit && t.unitLabels?.[unit as keyof typeof t.unitLabels]) {
      return t.unitLabels[unit as keyof typeof t.unitLabels] as string
    }
    return unit
  }

  // Use the zoning metrics hook for state management
  const {
    isEditing,
    metricGroups,
    overrides,
    hasOverrides,
    hasPendingChanges,
    setIsEditing,
    handleMetricChange,
    handleMetricClear,
    handleMetricGroupChange,
    handleSave,
    handleCancel,
    handleReset,
    getEffectiveValue,
    isValueOverridden,
    isValuePending,
    getAuthorizedSbd,
    getAuthorizedSb,
    getAuthorizedSpd,
    getAuthorizedSbpu,
    getAuthorizedSp,
    getAuthorizedVbr,
    isMetricVisible,
    toggleMetricVisibility,
  } = useZoningMetrics({
    zone,
    zoningType,
    landId,
    landData,
    zoningOverridesService,
  })

  // Notify parent when effective distance_limit changes (for map buffer)
  // Field may be _max or _min variant depending on zone data
  const zoneDistanceLimit =
    (zone as any).distance_limit_max_value ??
    (zone as any).distance_limit_min_value ??
    (zone as any).distance_limit_max ??
    (zone as any).distance_limit_min ??
    (zone as any).distance_limit_value ??
    (zone as any).distance_limit
  // Check for explicit overrides first (without fallback), then fall back to zone data
  const effectiveDistanceLimit =
    getEffectiveValue('distance_limit_max', undefined) ??
    getEffectiveValue('distance_limit_min', undefined) ??
    (typeof zoneDistanceLimit === 'number' ? zoneDistanceLimit : undefined)
  useEffect(() => {
    onDistanceLimitChange?.(effectiveDistanceLimit)
  }, [effectiveDistanceLimit, onDistanceLimitChange])

  // Use metric categories hook for organizing metrics
  // Pass overrides so metrics with only user overrides (no zone data) still appear in view mode
  const {
    allMetrics,
    hasMetrics,
    metricsByCategory,
    allPossibleMetrics,
    allPossibleNonExclusiveMetrics,
  } = useMetricCategories({ zone, overrides })

  // Use metric modal hook for modal state
  const {
    isOpen: isModalOpen,
    selectedMetric,
    handleAuthorizedClick,
    handleCloseModal,
  } = useMetricModal({ allMetrics })

  // Estimate stats for ALG justification panel
  const [estimateStats, setEstimateStats] =
    useState<ZoningEstimateStats | null>(null)
  const [estimateStatsLoading, setEstimateStatsLoading] = useState(false)

  // Envelope calculation modal (anchor row click)
  const [envelopeModalOpen, setEnvelopeModalOpen] = useState(false)

  // Merged regulation references for the envelope modal's PDF panel: every
  // envelope input (setbacks, height, length/depth) may cite a different
  // article — list them all, deduplicated, labeled with the metric(s) they
  // back. The PDF opens on the first (the setback drives the footprint);
  // the others get "View PDF" links via the existing reference list.
  const envelopeSources: ZoningMetricSources | undefined = React.useMemo(() => {
    if (!envelopeActive) return undefined
    const inputKeys = [
      'small_setback',
      'distance_limit',
      'big_setback',
      'building_total_height',
      'building_without_roof_height',
      'length',
      'building_depth',
    ]
    const merged = new Map<string, { ref: ZoningReference; labels: string[] }>()
    for (const key of inputKeys) {
      const bundle =
        getMetricMaxSources(zone, key) ??
        getMetricMinSources(zone, key) ??
        getMetricSources(zone, key)
      const refs = bundle?.llm?.reference
      if (!refs?.length) continue
      const label = getTranslatedMetricLabel(key)
      for (const ref of refs) {
        const id = `${ref.document_id || ref.document_url || ''}|${ref.article || ''}|${ref.page_start || ''}`
        const existing = merged.get(id)
        if (existing) {
          if (!existing.labels.includes(label)) existing.labels.push(label)
        } else {
          merged.set(id, { ref, labels: [label] })
        }
      }
    }
    if (merged.size === 0) return undefined
    return {
      llm: {
        reference: Array.from(merged.values()).map(({ ref, labels }) => ({
          ...ref,
          // Rendered as the reference card's title in MetricSourceModal
          metric_label: labels.join(', '),
        })),
      },
    } as ZoningMetricSources
  }, [envelopeActive, zone])

  // Exclusive ratio concepts (occupation / utilization): visibility is the
  // eye toggle like everywhere else; metric_group_selections keeps only the
  // naming variant. Legacy saved 'none' reads as hidden.
  const conceptDefaultVisible = (g: 'group1' | 'group2') =>
    !envelopeActive && metricGroups[g] !== 'none'
  const conceptVisible = (g: 'group1' | 'group2') =>
    isMetricVisible(g, conceptDefaultVisible(g))
  const conceptSelectedKey = (
    g: 'group1' | 'group2',
    options: { key: string; metric?: (typeof allMetrics)[number] }[]
  ): string => {
    const sel = metricGroups[g]
    if (sel && sel !== 'none') return sel
    const populated = options.find(
      (o) => o.metric?.max !== undefined || o.metric?.min !== undefined
    )
    return (populated ?? options[0])?.key || ''
  }

  // Live envelope recompute in edit mode. Height is linear (exact); a
  // setback override previews via the shared turf erosion (uniform case).
  const envelopeLive: EnvelopeLiveValues | undefined = React.useMemo(() => {
    if (!envelopeActive || !isEditing || !landData || readOnly) return undefined
    // Setback metrics are min-typed, heights max-typed — read either bound.
    const boundValue = (key: string) => {
      const m = allMetrics.find((mm) => mm.key === key)
      return m?.max ?? m?.min
    }
    const zoneHeight = boundValue('building_total_height')
    const zoneSetback =
      boundValue('distance_limit') ?? boundValue('small_setback')
    const heightOverride =
      getEffectiveValue('building_total_height_max', undefined) ??
      getEffectiveValue('building_total_height_min', undefined)
    const setbackOverride =
      getEffectiveValue('distance_limit_max', undefined) ??
      getEffectiveValue('distance_limit_min', undefined) ??
      getEffectiveValue('small_setback_max', undefined) ??
      getEffectiveValue('small_setback_min', undefined)

    let footprintM2: number | undefined
    let reason: 'height' | 'setback' | undefined

    if (
      typeof setbackOverride === 'number' &&
      Number.isFinite(setbackOverride) &&
      setbackOverride !== zoneSetback &&
      landData.landGeoPolygon
    ) {
      const eroded = erodePolygonByMeters(
        landData.landGeoPolygon,
        setbackOverride
      )
      footprintM2 = eroded.areaM2
      reason = 'setback'
    }

    const heightChanged =
      typeof heightOverride === 'number' &&
      Number.isFinite(heightOverride) &&
      heightOverride !== zoneHeight
    const effectiveFootprint =
      footprintM2 ?? landData.buildable_area_effective ?? undefined
    const effectiveHeight = heightChanged ? heightOverride : zoneHeight

    let authorizedVolume: number | undefined
    if (
      (reason === 'setback' || heightChanged) &&
      typeof effectiveFootprint === 'number' &&
      typeof effectiveHeight === 'number'
    ) {
      authorizedVolume = computeEnvelopeAuthorizedVolume(
        effectiveFootprint,
        effectiveHeight
      )
      if (!reason) reason = 'height'
    }

    if (!reason) return undefined
    return { authorizedVolume, footprintM2, reason }
  }, [
    envelopeActive,
    isEditing,
    readOnly,
    landData,
    allMetrics,
    getEffectiveValue,
  ])

  useEffect(() => {
    if (!isModalOpen || !selectedMetric || !onFetchEstimateStats) {
      setEstimateStats(null)
      return
    }

    // Check if this metric has an estimate source
    const zoneData = zone as any
    const key = selectedMetric.key
    const maxSource = zoneData[`${key}_max_source`]
    const minSource = zoneData[`${key}_min_source`]
    const defaultSource =
      zoneData[`${key}_default_source`] || zoneData[`${key}_max_default_source`]
    const sources = zoneData[`${key}_sources`] || zoneData[`${key}_max_sources`]
    const hasEstimate =
      maxSource === 'estimate' ||
      minSource === 'estimate' ||
      defaultSource === 'estimate' ||
      sources?.estimate?.value != null

    if (!hasEstimate || !zone._id) return

    setEstimateStatsLoading(true)
    onFetchEstimateStats(zoningType, zone._id, key)
      .then(setEstimateStats)
      .catch(() => setEstimateStats(null))
      .finally(() => setEstimateStatsLoading(false))
  }, [isModalOpen, selectedMetric, zone, zoningType, onFetchEstimateStats])

  // In read-only mode, never show editing UI
  const effectiveIsEditing = readOnly ? false : isEditing

  // Handle save button
  const handleSaveClick = async () => {
    try {
      await handleSave()
    } catch (error) {
      console.error('Failed to save:', error)
    }
  }

  // Handle reset button
  const handleResetClick = async () => {
    if (window.confirm(t.confirmReset || '')) {
      try {
        await handleReset()
      } catch (error) {
        console.error('Failed to reset:', error)
      }
    }
  }

  // Edit-mode rows for the two exclusive concepts. One row per concept:
  // eye toggle for visibility, compact selector for the naming variant —
  // replaces the radio lists + "None (hide this metric)" rows.
  const renderConceptRow = (
    g: 'group1' | 'group2',
    list: typeof allMetrics,
    conceptLabel: string
  ) => {
    const options = list.map((m) => ({
      key: m.key,
      label: getTranslatedMetricLabel(m.key),
      metric: m,
    }))
    if (options.length === 0) return null

    const selKey = conceptSelectedKey(g, options)
    if (!selKey) return null
    const selMetric = options.find((o) => o.key === selKey)?.metric
    const hidden = !conceptVisible(g)
    const currentKey = (selKey === 'im' ? 'mass_index' : selKey) as
      | keyof ZoningLandData
      | 'mass_index'

    const handleToggle = () => {
      const visible = conceptVisible(g)
      if (!visible && metricGroups[g] === 'none') {
        // Re-showing a legacy-hidden concept: restore a concrete variant
        handleMetricGroupChange(g, selKey)
      }
      toggleMetricVisibility(g, visible)
    }

    return (
      <ConceptMetricRow
        key={g}
        conceptLabel={conceptLabel}
        options={options}
        selectedKey={selKey}
        onVariantChange={(key) => handleMetricGroupChange(g, key)}
        isHidden={hidden}
        onToggleVisibility={handleToggle}
        hideTooltip={t.visibility?.hide}
        showTooltip={t.visibility?.show}
        subNote={
          envelopeActive && !hidden
            ? isValueOverridden(`${selKey}_max`)
              ? t.envelope?.manualReplacesSub
              : t.envelope?.referenceOnlySub
            : undefined
        }
        currentValue={
          landData?.[currentKey as keyof ZoningLandData] as number | undefined
        }
        minValue={getEffectiveValue(`${selKey}_min`, selMetric?.min)}
        maxValue={getEffectiveValue(`${selKey}_max`, selMetric?.max)}
        isMinOverridden={isValueOverridden(`${selKey}_min`)}
        isMaxOverridden={isValueOverridden(`${selKey}_max`)}
        isMinPending={isValuePending(`${selKey}_min`)}
        isMaxPending={isValuePending(`${selKey}_max`)}
        minSource={getMetricMinDefaultSource(zone, selKey)}
        maxSource={getMetricMaxDefaultSource(zone, selKey)}
        unit={getTranslatedUnit(selKey)}
        onValueChange={handleMetricChange}
        onValueClear={handleMetricClear}
      />
    )
  }

  const renderConceptDerived = (g: 'group1' | 'group2') => {
    if (!conceptVisible(g)) return null
    const sel = metricGroups[g]
    switch (sel) {
      case 'ios':
        return (
          <DerivedSurfaceRow
            title={t.derivedSurfaces?.determiningBuiltArea || ''}
            currentValue={landData?.sbd}
            authorizedValue={getAuthorizedSbd()}
            unit="m²"
            progressBarTranslations={t.progressBar}
          />
        )
      case 'cos':
        return (
          <DerivedSurfaceRow
            title={t.derivedSurfaces?.builtArea || ''}
            currentValue={landData?.sb}
            authorizedValue={getAuthorizedSb()}
            unit="m²"
            progressBarTranslations={t.progressBar}
          />
        )
      case 'ius':
        return (
          <DerivedSurfaceRow
            title={t.derivedSurfaces?.determiningFloorArea || ''}
            currentValue={landData?.spd}
            authorizedValue={getAuthorizedSpd()}
            unit="m²"
            progressBarTranslations={t.progressBar}
          />
        )
      case 'cus':
        return (
          <DerivedSurfaceRow
            title={t.derivedSurfaces?.grossUsefulFloorArea || ''}
            currentValue={landData?.sbpu}
            authorizedValue={getAuthorizedSbpu()}
            unit="m²"
            progressBarTranslations={t.progressBar}
          />
        )
      case 'ibus':
        return (
          <DerivedSurfaceRow
            title={t.derivedSurfaces?.floorArea || ''}
            currentValue={landData?.sp}
            authorizedValue={getAuthorizedSp()}
            unit="m²"
            progressBarTranslations={t.progressBar}
          />
        )
      case 'im':
        return (
          <DerivedSurfaceRow
            title={t.derivedSurfaces?.builtVolume || ''}
            currentValue={landData?.vbr}
            authorizedValue={getAuthorizedVbr()}
            unit="m³"
            progressBarTranslations={t.progressBar}
          />
        )
      default:
        return null
    }
  }

  const renderConceptRows = () => (
    <>
      {renderConceptRow(
        'group1',
        allPossibleMetrics.occupation,
        t.concepts?.occupation || 'Site coverage'
      )}
      {renderConceptDerived('group1')}
      {renderConceptRow(
        'group2',
        allPossibleMetrics.utilization,
        t.concepts?.utilization || 'Floor-area ratio'
      )}
      {renderConceptDerived('group2')}
    </>
  )

  // Helper to render a standard metric section (heights, floors, setbacks…).
  // viewMetrics = populated metrics (what view mode shows, minus hidden ones);
  // editMetrics = every possible metric of the section. In edit mode,
  // populated/explicitly-added metrics render as rows with an eye toggle and
  // the rest collapse into "+ Add metric" chips (no more walls of empty
  // 0.00 inputs).
  const renderMetricSection = (
    title: string,
    viewMetrics: typeof allMetrics,
    editMetrics?: typeof allMetrics
  ) => {
    const populatedKeys = new Set(viewMetrics.map((m) => m.key))
    const rows = effectiveIsEditing
      ? (editMetrics || viewMetrics).filter(
          (m) => populatedKeys.has(m.key) || isMetricVisible(m.key, false)
        )
      : viewMetrics.filter((m) => isMetricVisible(m.key, true))
    const addable = effectiveIsEditing
      ? (editMetrics || []).filter(
          (m) => !populatedKeys.has(m.key) && !isMetricVisible(m.key, false)
        )
      : []

    if (rows.length === 0 && addable.length === 0) return null

    return (
      <>
        <MetricGroupHeader title={title} />
        {rows.map((metric) => {
          const effectiveMinValue = getEffectiveValue(
            `${metric.key}_min`,
            metric.min
          )
          const effectiveMaxValue = getEffectiveValue(
            `${metric.key}_max`,
            metric.max
          )
          const rowDefaultVisible = populatedKeys.has(metric.key)
          const rowVisible = isMetricVisible(metric.key, rowDefaultVisible)

          return (
            <StandardMetricRow
              key={metric.key}
              metricKey={metric.key}
              metricLabel={getTranslatedMetricLabel(metric.key)}
              metricDescription={METRIC_DEFINITIONS[metric.key]?.label}
              isEditing={effectiveIsEditing}
              currentValue={getCurrentValueFromLandData(metric.key, landData)}
              minValue={effectiveMinValue}
              maxValue={effectiveMaxValue}
              minSource={metric.minSource}
              maxSource={metric.maxSource}
              defaultSource={getMetricDefaultSource(zone, metric.key)}
              agreement={getMetricAgreement(zone, metric.key)}
              sources={getMetricSources(zone, metric.key)}
              minDefaultSource={getMetricMinDefaultSource(zone, metric.key)}
              maxDefaultSource={getMetricMaxDefaultSource(zone, metric.key)}
              minSources={getMetricMinSources(zone, metric.key)}
              maxSources={getMetricMaxSources(zone, metric.key)}
              minAgreement={getMetricMinAgreement(zone, metric.key)}
              maxAgreement={getMetricMaxAgreement(zone, metric.key)}
              isOverridden={isValueOverridden(`${metric.key}_max`)}
              isPending={isValuePending(`${metric.key}_max`)}
              isMinOverridden={isValueOverridden(`${metric.key}_min`)}
              isMinPending={isValuePending(`${metric.key}_min`)}
              unit={getTranslatedUnit(metric.key)}
              showBorder={false}
              subNote={
                envelopeActive && ENVELOPE_INPUT_KEYS.has(metric.key)
                  ? t.envelope?.inputTag
                  : undefined
              }
              showVisibilityToggle={effectiveIsEditing}
              isRowHidden={!rowVisible}
              onToggleVisibility={() =>
                toggleMetricVisibility(metric.key, rowVisible)
              }
              hideTooltip={t.visibility?.hide}
              showTooltip={t.visibility?.show}
              onValueChange={handleMetricChange}
              onValueClear={handleMetricClear}
              onAuthorizedClick={handleAuthorizedClick}
            />
          )
        })}
        {addable.length > 0 && (
          <AddMetricRow
            label={t.visibility?.addMetric || 'Add metric'}
            options={addable.map((m) => ({
              key: m.key,
              label: getTranslatedMetricLabel(m.key),
            }))}
            onAdd={(key) => toggleMetricVisibility(key, false)}
          />
        )}
      </>
    )
  }

  if (!hasMetrics && !landData?.std && !hasOverrides) {
    return null
  }

  return (
    <Section>
      {/* Header with edit controls */}
      <SectionHeader>
        <SectionTitle>{t.title}</SectionTitle>
        {!readOnly && (
          <ButtonGroup>
            {!isEditing ? (
              <>
                {hasOverrides && (
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleResetClick}
                  >
                    {t.resetToDefault}
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => setIsEditing(true)}
                >
                  {t.editAuthorizedValues}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" size="small" onClick={handleCancel}>
                  {t.cancel}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSaveClick}
                  disabled={!hasPendingChanges}
                >
                  {t.saveChanges}
                </Button>
              </>
            )}
          </ButtonGroup>
        )}
      </SectionHeader>

      <Table>
        <TableHead>
          <tr>
            <th>{t.headers?.indices}</th>
            <th>{t.headers?.current}</th>
            <th>{t.headers?.authorized}</th>
            <th>{t.headers?.difference}</th>
          </tr>
        </TableHead>

        <TableBody>
          {/* INDICES SECTION */}
          {(landData?.std !== undefined ||
            envelopeActive ||
            metricsByCategory.indices.occupation.length > 0 ||
            metricsByCategory.indices.utilization.length > 0) && (
            <>
              {/* STd - Determining land area (always first) */}
              {landData?.std !== undefined && (
                <DeterminingAreaRow
                  std={landData.std}
                  label={t.determiningLandArea}
                />
              )}

              {/* S7 fallback note: official rules exist but the envelope
                  branch refused this land — the score stays an estimate */}
              {!envelopeActive &&
                !effectiveIsEditing &&
                typeof landData?.indice_used === 'string' &&
                landData.indice_used.endsWith('_estimate') &&
                isEnvelopeFallback(allMetrics) &&
                t.envelope?.s7Note && (
                  <tr>
                    <td
                      colSpan={4}
                      style={{ borderBottom: 'none', paddingTop: 0 }}
                    >
                      <EmptyValue>{t.envelope.s7Note}</EmptyValue>
                    </td>
                  </tr>
                )}

              {/* Regelbauweise envelope block — replaces the estimated
                  ratio rows on envelope-scored lands. Hideable like any
                  other metric (display only, never affects the score). */}
              {envelopeActive &&
                landData &&
                (effectiveIsEditing || isMetricVisible('envelope', true)) && (
                  <EnvelopeSection
                    landData={landData}
                    isEditing={effectiveIsEditing}
                    live={envelopeLive}
                    translations={t.envelope}
                    progressBarTranslations={t.progressBar}
                    onAnchorClick={() => setEnvelopeModalOpen(true)}
                    showVisibilityToggle={effectiveIsEditing}
                    isHidden={!isMetricVisible('envelope', true)}
                    onToggleVisibility={() =>
                      toggleMetricVisibility(
                        'envelope',
                        isMetricVisible('envelope', true)
                      )
                    }
                    hideTooltip={t.visibility?.hide}
                    showTooltip={t.visibility?.show}
                  />
                )}

              {/* Occupation metrics (IOS/COS) — edit mode renders one row
                  per concept (eye + variant selector) via renderConceptRows */}
              {effectiveIsEditing && renderConceptRows()}

              {/* On envelope lands the groups default to 'none' (estimates
                  would contradict the envelope score) — a saved opt-in
                  selection still shows the row for reference. */}
              {(effectiveIsEditing
                ? []
                : conceptVisible('group1')
                  ? metricsByCategory.indices.occupation
                  : []
              ).map((metric) => {
                const isSelected = metricGroups.group1 === metric.key
                const shouldShow = isSelected || effectiveIsEditing

                if (!shouldShow) return null

                const effectiveMinValue = getEffectiveValue(
                  `${metric.key}_min`,
                  metric.min
                )
                const effectiveMaxValue = getEffectiveValue(
                  `${metric.key}_max`,
                  metric.max
                )

                return (
                  <React.Fragment key={metric.key}>
                    <MetricGroupRow
                      metricKey={metric.key}
                      metricLabel={getTranslatedMetricLabel(metric.key)}
                      metricDescription={METRIC_DEFINITIONS[metric.key]?.label}
                      groupName="group1"
                      isSelected={isSelected}
                      isEditing={effectiveIsEditing}
                      subNote={
                        envelopeActive && effectiveIsEditing
                          ? isSelected &&
                            !isValueOverridden(`${metric.key}_max`)
                            ? t.envelope?.referenceOnlySub
                            : t.envelope?.manualReplacesSub
                          : undefined
                      }
                      currentValue={
                        landData?.[metric.key as keyof ZoningLandData] as
                          | number
                          | undefined
                      }
                      minValue={effectiveMinValue}
                      maxValue={effectiveMaxValue}
                      minSource={metric.minSource}
                      maxSource={metric.maxSource}
                      defaultSource={getMetricDefaultSource(zone, metric.key)}
                      agreement={getMetricAgreement(zone, metric.key)}
                      sources={getMetricSources(zone, metric.key)}
                      minDefaultSource={getMetricMinDefaultSource(
                        zone,
                        metric.key
                      )}
                      maxDefaultSource={getMetricMaxDefaultSource(
                        zone,
                        metric.key
                      )}
                      minSources={getMetricMinSources(zone, metric.key)}
                      maxSources={getMetricMaxSources(zone, metric.key)}
                      minAgreement={getMetricMinAgreement(zone, metric.key)}
                      maxAgreement={getMetricMaxAgreement(zone, metric.key)}
                      isOverridden={isValueOverridden(`${metric.key}_max`)}
                      isPending={isValuePending(`${metric.key}_max`)}
                      isMinOverridden={isValueOverridden(`${metric.key}_min`)}
                      isMinPending={isValuePending(`${metric.key}_min`)}
                      unit={getTranslatedUnit(metric.key)}
                      onGroupChange={handleMetricGroupChange}
                      onValueChange={handleMetricChange}
                      onValueClear={handleMetricClear}
                      onAuthorizedClick={handleAuthorizedClick}
                      status={metric?.maxReference?.status}
                      maxReference={metric?.maxReference}
                    />

                    {isSelected && metric.key === 'ios' && (
                      <DerivedSurfaceRow
                        title={t.derivedSurfaces?.determiningBuiltArea || ''}
                        description={
                          t.derivedSurfaces?.determiningBuiltAreaDesc || ''
                        }
                        currentValue={landData?.sbd}
                        authorizedValue={getAuthorizedSbd()}
                        unit="m²"
                        progressBarTranslations={t.progressBar}
                      />
                    )}

                    {isSelected && metric.key === 'cos' && (
                      <DerivedSurfaceRow
                        title={t.derivedSurfaces?.builtArea || ''}
                        description={t.derivedSurfaces?.builtAreaDesc || ''}
                        currentValue={landData?.sb}
                        authorizedValue={getAuthorizedSb()}
                        unit="m²"
                        progressBarTranslations={t.progressBar}
                      />
                    )}
                  </React.Fragment>
                )
              })}

              {/* Utilization metrics (IUS/CUS/IBUS/IM) */}

              {(effectiveIsEditing
                ? []
                : conceptVisible('group2')
                  ? metricsByCategory.indices.utilization
                  : []
              ).map((metric) => {
                const isSelected = metricGroups.group2 === metric.key
                const shouldShow = isSelected || effectiveIsEditing

                if (!shouldShow) return null

                const effectiveMinValue = getEffectiveValue(
                  `${metric.key}_min`,
                  metric.min
                )
                const effectiveMaxValue = getEffectiveValue(
                  `${metric.key}_max`,
                  metric.max
                )

                return (
                  <React.Fragment key={metric.key}>
                    <MetricGroupRow
                      metricKey={metric.key}
                      metricLabel={getTranslatedMetricLabel(metric.key)}
                      metricDescription={METRIC_DEFINITIONS[metric.key]?.label}
                      groupName="group2"
                      isSelected={isSelected}
                      isEditing={effectiveIsEditing}
                      subNote={
                        envelopeActive && effectiveIsEditing
                          ? isSelected &&
                            !isValueOverridden(`${metric.key}_max`)
                            ? t.envelope?.referenceOnlySub
                            : t.envelope?.manualReplacesSub
                          : undefined
                      }
                      currentValue={(() => {
                        // List allowed keys for ZoningLandData
                        type LandDataKey = keyof ZoningLandData
                        const key: LandDataKey =
                          metric.key === 'im'
                            ? 'mass_index'
                            : (metric.key as LandDataKey)
                        return landData?.[key] as number | undefined
                      })()}
                      minValue={effectiveMinValue}
                      maxValue={effectiveMaxValue}
                      minSource={metric.minSource}
                      maxSource={metric.maxSource}
                      defaultSource={getMetricDefaultSource(zone, metric.key)}
                      agreement={getMetricAgreement(zone, metric.key)}
                      sources={getMetricSources(zone, metric.key)}
                      minDefaultSource={getMetricMinDefaultSource(
                        zone,
                        metric.key
                      )}
                      maxDefaultSource={getMetricMaxDefaultSource(
                        zone,
                        metric.key
                      )}
                      minSources={getMetricMinSources(zone, metric.key)}
                      maxSources={getMetricMaxSources(zone, metric.key)}
                      minAgreement={getMetricMinAgreement(zone, metric.key)}
                      maxAgreement={getMetricMaxAgreement(zone, metric.key)}
                      isOverridden={isValueOverridden(`${metric.key}_max`)}
                      isPending={isValuePending(`${metric.key}_max`)}
                      isMinOverridden={isValueOverridden(`${metric.key}_min`)}
                      isMinPending={isValuePending(`${metric.key}_min`)}
                      unit={getTranslatedUnit(metric.key)}
                      onGroupChange={handleMetricGroupChange}
                      onValueChange={handleMetricChange}
                      onValueClear={handleMetricClear}
                      onAuthorizedClick={handleAuthorizedClick}
                      status={metric?.maxReference?.status}
                      maxReference={metric?.maxReference}
                    />

                    {isSelected && metric.key === 'ius' && (
                      <DerivedSurfaceRow
                        title={t.derivedSurfaces?.determiningFloorArea || ''}
                        description={
                          t.derivedSurfaces?.determiningFloorAreaDesc || ''
                        }
                        currentValue={landData?.spd}
                        authorizedValue={getAuthorizedSpd()}
                        unit="m²"
                        progressBarTranslations={t.progressBar}
                      />
                    )}

                    {isSelected && metric.key === 'cus' && (
                      <DerivedSurfaceRow
                        title={t.derivedSurfaces?.grossUsefulFloorArea || ''}
                        description={
                          t.derivedSurfaces?.grossUsefulFloorAreaDesc || ''
                        }
                        currentValue={landData?.sbpu}
                        authorizedValue={getAuthorizedSbpu()}
                        unit="m²"
                        progressBarTranslations={t.progressBar}
                      />
                    )}

                    {isSelected && metric.key === 'ibus' && (
                      <DerivedSurfaceRow
                        title={t.derivedSurfaces?.floorArea || ''}
                        description={t.derivedSurfaces?.floorAreaDesc || ''}
                        currentValue={landData?.sp}
                        authorizedValue={getAuthorizedSp()}
                        unit="m²"
                        progressBarTranslations={t.progressBar}
                      />
                    )}

                    {isSelected && metric.key === 'im' && (
                      <DerivedSurfaceRow
                        title={t.derivedSurfaces?.builtVolume || ''}
                        description={t.derivedSurfaces?.builtVolumeDesc || ''}
                        currentValue={landData?.vbr}
                        authorizedValue={getAuthorizedVbr()}
                        unit="m³"
                        progressBarTranslations={t.progressBar}
                      />
                    )}
                  </React.Fragment>
                )
              })}
            </>
          )}

          {/* HEIGHTS SECTION */}
          {(effectiveIsEditing || metricsByCategory.heights.length > 0) &&
            renderMetricSection(
              t.groups?.heights || '',
              metricsByCategory.heights,
              allPossibleNonExclusiveMetrics.heights
            )}

          {/* FLOORS SECTION */}
          {(effectiveIsEditing || metricsByCategory.floors.length > 0) &&
            renderMetricSection(
              t.groups?.floors || '',
              metricsByCategory.floors,
              allPossibleNonExclusiveMetrics.floors
            )}

          {/* SETBACKS & DIMENSIONS SECTION */}
          {(effectiveIsEditing || metricsByCategory.setbacks.length > 0) &&
            renderMetricSection(
              t.groups?.setbacks || '',
              metricsByCategory.setbacks,
              allPossibleNonExclusiveMetrics.setbacks
            )}

          {/* USAGE RATIOS SECTION */}
          {(effectiveIsEditing || metricsByCategory.usageRatios.length > 0) &&
            renderMetricSection(
              t.groups?.usageRatios || '',
              metricsByCategory.usageRatios,
              allPossibleNonExclusiveMetrics.usage
            )}

          {/* ENVIRONMENTAL SECTION */}
          {(effectiveIsEditing || metricsByCategory.environmental.length > 0) &&
            renderMetricSection(
              t.groups?.environmental || '',
              metricsByCategory.environmental,
              allPossibleNonExclusiveMetrics.environmental
            )}

          {/* OTHER SECTION */}
          {(effectiveIsEditing || metricsByCategory.other.length > 0) &&
            renderMetricSection(
              t.groups?.other || '',
              metricsByCategory.other,
              allPossibleNonExclusiveMetrics.other
            )}
        </TableBody>
      </Table>

      {/* Envelope calculation modal (Regelbauweise anchor row) */}
      {envelopeActive && landData && envelopeModalOpen && (
        <MetricSourceModal
          isOpen={envelopeModalOpen}
          onClose={() => setEnvelopeModalOpen(false)}
          metricLabel={
            t.envelope?.maxBuildingVolume || 'Maximum building volume'
          }
          value={landData.volume_envelope_authorized ?? undefined}
          unit="m³"
          defaultSource={
            (getEnvelopeSource(landData.indice_used) ||
              'llm') as zoning_indice_sources
          }
          sources={
            envelopeSources ||
            getMetricMaxSources(zone, 'distance_limit') ||
            getMetricSources(zone, 'distance_limit') ||
            getMetricMaxSources(zone, 'small_setback') ||
            getMetricSources(zone, 'small_setback')
          }
          landId={landId}
          zoneId={zone._id}
          zoneName={(zone as any).name}
          zoneType={zoningType}
          envelopeCalculation={{
            calculatedValueLabel:
              t.envelope?.calculatedValueLabel || 'Calculated value',
            title:
              t.envelope?.calcTitle ||
              'Calculation — standard construction rules',
            rows: buildEnvelopeCalculationRows(
              landData,
              allMetrics,
              t.envelope?.calc,
              (key, bound) =>
                bound === 'max'
                  ? (getMetricMaxDefaultSource(zone, key) ??
                    getMetricDefaultSource(zone, key))
                  : (getMetricMinDefaultSource(zone, key) ??
                    getMetricDefaultSource(zone, key))
            ),
            disclaimer:
              t.envelope?.disclaimer ||
              'This is a well-founded approximation of the legal envelope. ' +
                'Road setbacks, distances to neighbouring buildings, terrain ' +
                'slope and roof-shape rules are governed separately and not ' +
                'modeled.',
          }}
          onReportIncorrect={onReportIncorrect}
        />
      )}

      {/* Modal for showing metric source details */}
      {selectedMetric &&
        (() => {
          const baseProps = getModalProps(
            selectedMetric,
            zone,
            getTranslatedMetricLabel(selectedMetric.key),
            isModalOpen,
            handleCloseModal,
            landData,
            zoningType,
            landId
          )

          // If the metric has been overridden by the user, inject MAN source
          const baseKey = selectedMetric.key
          // Derive the suffixed key for parseMetricKey (e.g., floor_nb → floor_nb_max)
          // Default to _max when both min/max are undefined (N/A case)
          const metricKey =
            selectedMetric.max !== undefined
              ? `${baseKey}_max`
              : selectedMetric.min !== undefined
                ? `${baseKey}_min`
                : `${baseKey}_max`
          const isOverridden =
            isValueOverridden(metricKey) ||
            isValueOverridden(`${metricKey}_max`)

          if (isOverridden) {
            const originalValue = selectedMetric.max ?? selectedMetric.min ?? 0
            const overrideValueMax = getEffectiveValue(
              `${metricKey}_max`,
              originalValue
            )
            const overrideValueBase = getEffectiveValue(
              metricKey,
              originalValue
            )
            const overrideValue =
              overrideValueMax !== originalValue
                ? overrideValueMax
                : overrideValueBase

            const hasMinMax =
              selectedMetric.min !== undefined &&
              selectedMetric.max !== undefined
            const manualSources = { manual: { value: overrideValue } }

            if (hasMinMax) {
              const isMinOverride = isValueOverridden(`${baseKey}_min`)
              const isMaxOverride = isValueOverridden(`${baseKey}_max`)
              return (
                <MetricSourceModal
                  {...baseProps}
                  minSources={
                    isMinOverride
                      ? { ...(baseProps.minSources || {}), ...manualSources }
                      : baseProps.minSources
                  }
                  maxSources={
                    isMaxOverride
                      ? { ...(baseProps.maxSources || {}), ...manualSources }
                      : baseProps.maxSources
                  }
                  minDefaultSource={
                    isMinOverride ? 'manual' : baseProps.minDefaultSource
                  }
                  maxDefaultSource={
                    isMaxOverride ? 'manual' : baseProps.maxDefaultSource
                  }
                  value={overrideValue}
                  unit={getTranslatedUnit(metricKey)}
                  onReportIncorrect={onReportIncorrect}
                  estimateStats={estimateStats}
                  estimateStatsLoading={estimateStatsLoading}
                  metricKey={metricKey}
                />
              )
            }

            return (
              <MetricSourceModal
                {...baseProps}
                defaultSource="manual"
                sources={{ ...(baseProps.sources || {}), ...manualSources }}
                value={overrideValue}
                unit={getTranslatedUnit(metricKey)}
                onReportIncorrect={onReportIncorrect}
                estimateStats={estimateStats}
                estimateStatsLoading={estimateStatsLoading}
                metricKey={metricKey}
              />
            )
          }

          return (
            <MetricSourceModal
              {...baseProps}
              unit={getTranslatedUnit(metricKey)}
              onReportIncorrect={onReportIncorrect}
              estimateStats={estimateStats}
              estimateStatsLoading={estimateStatsLoading}
              metricKey={metricKey}
            />
          )
        })()}
    </Section>
  )
}
